import { assert, error, log } from '../app/logger.js';
import { ReportDialog } from '../controls/report_dialog.js';
import { locationTaxonomy } from '../data/location_taxonomy.js';
import { Picture } from '../data/picture.js';
import { listenToEvent, listenToEventSync } from '../util/listen_to_event.js';
import { LocBase } from './loc_base.js';
import { LocDeleteDialog } from './loc_delete_dialog.js';
import { LocMarkupEdit } from './loc_markup_edit.js';

import '../controls/list_control.js';
import '../controls/spreadsheet_control.js';
import '../media/picupload_list.js';
import '../map/pick_loc_control.js';
import '../map/pick_track_control.js';
import './loctype_control.js';

/**
 * Location edit view.
 */
class LocEdit extends LocBase {
  static get selectors() {
    return this.MarkupClass.selectors;
  }

  static get elements() {
    const s = this.selectors;
    return {
      attributeContainer: s.AttributeContainer,
      cancelButton: s.CancelButton,
      deleteOrReportButton: s.DeleteOrReportButton,
      descriptionTextarea: s.DescriptionTextarea,
      fieldsSpreadsheet: s.FieldsSpreadsheet,
      formElement: s.Form,
      latInput: s.LatInput,
      lngInput: s.LngInput,
      mobileBackButton: s.MobileBackButton,
      mobileSaveButton: s.MobileSaveButton,
      nameInput: s.NameInput,
      picListElement: s.PicList,
      pickTrackContainer: s.PickTrackContainer,
      pickTrackElement: s.PickTrack,
      relContainer: s.RelContainer,
      relElements: s.Rels,
      relOrRelSetElementByType: type => s.RelOrRelSetByType(type),
      relSetElements: s.RelSets,
      typeElement: s.Type,
      saveButton: s.SaveButton,
      saveSubmitButton: s.SaveSubmitButton,
      stateCheckbox: s.StateCheckbox,
      stateCheckboxContainer: s.StateCheckboxContainer,
    };
  }

  static get MarkupClass() {
    return LocMarkupEdit;
  }

  static tagName = 'loc-edit';

  constructor() {
    super();

    if (!this.#editModeDropDefault) {
      this.#editModeDropDefault = ev => ev.preventDefault();
      this.ownerDocument.addEventListener('drop', this.#editModeDropDefault);
    }
  }

  set(loc) {
    assert(loc, `[locview] LocViewEdit.show: no loc is given`);

    if (!super.set(loc)) {
      return;
    }

    this.innerHTML = this.markupGenerator.editMarkup;

    // Cover
    this.setCover(this.loc.midsizeCoverUrl);

    // Lat/lng fields
    listenToEventSync(this.latInput, 'paste', ev => this.#locValuePasted(ev));
    listenToEventSync(this.lngInput, 'paste', ev => this.#locValuePasted(ev));

    listenToEvent(this.latInput, 'change', () => this.#firePosChange());
    listenToEvent(this.lngInput, 'change', () => this.#firePosChange());

    // Update/cancel/delete
    listenToEvent(this.cancelButton, 'click', () => this.#cancelEditing());
    listenToEvent(this.mobileBackButton, 'click', () => this.#cancelEditing());

    listenToEvent(this.saveButton, 'click', () => this.#saveOnClick());
    listenToEvent(this.mobileSaveButton, 'click', () => this.#saveOnClick());

    listenToEvent(this.formElement, 'submit', () => this.#save(), {
      preventDefault: true,
    });

    listenToEvent(this.deleteOrReportButton, 'click', () =>
      this.#deleteOrReport()
    );

    // Autoadjust category for a new location.
    let setType = true;
    if (!this.loc.id) {
      setType = !this.#guessLocTypeFromName(this.loc.name);
      listenToEvent(this.nameInput, 'change', ev =>
        this.#guessLocTypeFromName(ev.target.value)
      );
    }
    if (setType) {
      this.typeElement.set(this.loc.type, this.loc.subtype);
      this.setTypeDependentControls(this.loc.type);
    }

    listenToEvent(this.typeElement, 'itemselect', ev =>
      this.setTypeDependentControls(ev.detail && ev.detail.category)
    );

    if (this.loc.fields) {
      if (this.loc.fields instanceof Array) {
        for (let entry of this.loc.fields) {
          this.fieldsSpreadsheet.appendRow(entry);
        }
      }
    }

    // Pics
    this.picListElement.pics = this.loc.pics;
    this.picListElement.addEventListener('picselect', ev => {
      this.changeCover(ev.detail);
    });
    this.picListElement.addEventListener('picupload', ev => {
      if (!this.loc.cover) {
        this.changeCover(ev.detail.item, ev.detail.picid, ev.detail.url);
      }
    });
    let coverIdx = this.loc.pics.indexOf(this.loc.cover);
    if (coverIdx != -1) {
      let picEl = this.picListElement.getItemElementAt(coverIdx);
      if (picEl) {
        picEl.classList.add('cover');
      }
    }
    this.#cover = this.loc.cover;

    // Make public checkbox
    this.stateCheckboxContainer.hidden = this.loc.state == 'public';
    this.stateCheckbox.checked = this.loc.state == 'private';

    window.setTimeout(() => this.nameInput.focus());

    this.scrollTop = 0;
  }

  setCoordinates({ lat, lng }) {
    this.latInput.value = lat.toFixed(7);
    this.lngInput.value = lng.toFixed(7);
  }

  setTypeDependentControls(type) {
    let typeInfo = locationTaxonomy.get({ type });
    this.createAttrControls(typeInfo);
    this.createRelControls(typeInfo);

    this.pickTrackContainer.hidden = !typeInfo.canHaveRoute();
    this.pickTrackElement.map = this.map;
    this.pickTrackElement.track = this.loc.gpxTracks && this.loc.gpxTracks[0];
  }

  createAttrControls(typeInfo) {
    this.#attrControls = {};
    for (let attrName in typeInfo.attributes) {
      let attrInfo = typeInfo.attributes[attrName];
      switch (attrInfo.type) {
        case 'list':
          {
            let listEl = document.createElement('list-control');
            listEl.id = `map-panel-edit-${attrName}`;
            listEl.label = attrInfo.title;
            listEl.options = attrInfo.values;
            listEl.noValidate = true;
            listEl.value = this.loc.attributesOf(attrName) || [];
            this.attributeContainer.append(listEl);
            this.#attrControls[attrName] = listEl;
          }
          break;

        default:
          break;
      }
    }
  }

  createRelControls(typeInfo) {
    const placeholder = 'Associate with';

    let markup = '';
    for (let rel of typeInfo.rels) {
      let type = typeof rel == 'string' ? rel : rel.type;
      let relType = (typeof rel != 'string' && rel.relType) || 'one-to-one';
      let relInfo = locationTaxonomy.get({ type });
      if (relType == 'one-to-one') {
        markup += `
          <label>${relInfo.category}:</label>
          <pick-loc-control type='${type}'
                            placeholder='${placeholder}'></pick-loc-control>
        `;
      } else {
        let plurarCategory = relInfo.category.endsWith('y')
          ? `${relInfo.category.substring(0, relInfo.category.length - 1)}ies`
          : `${relInfo.category}s`;
        markup += `
          <label>${plurarCategory}:</label>
          <spreadsheet-control type='${type}'>
            <template>
              <pick-loc-control type='${type}'
                                placeholder='${placeholder}'></pick-loc-control>
            </template>
          </spreadsheet-control>
      `;
      }
    }
    this.relContainer.innerHTML = markup;
    for (let el of this.relElements) {
      el.map = this.map;
    }
    for (let el of this.relSetElements) {
      el.addEventListener('cellinsert', ev => {
        let relEl = el.cellAt(ev.detail.rowIndex, ev.detail.columnIndex);
        relEl.map = this.map;
      });
    }

    // Set data.
    let relmap = (this.loc.rels || []).reduce((a, v) => {
      if (!a.has(v.type)) {
        a.set(v.type, []);
      }
      a.get(v.type).push(v);
      return a;
    }, new Map());

    for (let [type, rellist] of relmap.entries()) {
      let el = this.relOrRelSetElementByType(type);
      assert(el, `[locview] no element for '${type}' related location type`);

      let ltirel = typeInfo.rels.find(i => i == type || i.type == type);
      if (ltirel && ltirel.relType == 'one-to-many') {
        for (let rel of rellist) {
          let rowIndex = el.appendRow();
          let cellEl = el.firstCellOfRowAt(rowIndex);
          cellEl.datavalue = {
            id: rel.id,
            name: rel.name,
          };
        }
        continue;
      }

      assert(rellist.length == 1, `[locview] wrong relation number of ${type}`);
      el.datavalue = {
        id: rellist[0].id,
        name: rellist[0].name,
      };
    }
  }

  changeCover(picEl, picid, url) {
    let thumbnailCoverEl = this.picListElement.querySelector('.cover');
    if (thumbnailCoverEl) {
      thumbnailCoverEl.classList.remove('cover');
    }
    picEl.classList.add('cover');

    this.#cover = picid || this.picListElement.pics[picEl.index];
    this.setCover(url || Picture.getMidSizePicUrl(this.#cover));
  }

  /**
   * Cancels editing mode.
   */
  #cancelEditing() {
    this.dispatchEvent(
      new Event('editcancel', {
        bubbles: true,
      })
    );
  }

  /**
   * Updates the location info.
   */
  #save() {
    log(`[locview] LocViewEdit.save for '${this.loc.name}':${this.loc.id}`);

    let id = this.loc.id;
    let name = this.nameInput.value;
    let description = this.descriptionTextarea.value;
    let google_id = this.loc.google_id;
    let { category: type, type: subtype } = this.typeElement;
    let lat = this.latInput.valueAsNumber;
    let lng = this.lngInput.valueAsNumber;

    // Relations.
    let rels = Array.from(this.relElements)
      .filter(el => el.value && el.selectedData)
      .map(el => ({
        id: el.selectedData.id,
        name: el.selectedData.name,
        type: el.selectedData.type,
        lat: el.selectedData.lat,
        lng: el.selectedData.lng,
      }));
    let relatedIds = rels.map(rel => rel.id);

    let relsHash = rels.reduce((a, v) => {
      a.set(v.id, (a.get(v.id) || 0) + 1);
      return a;
    }, new Map());
    for (let [k, v] of relsHash.entries()) {
      if (v > 1) {
        error(
          `[map] dupe relation`,
          rels.find(rel => rel.id == k)
        );
        return;
      }
    }

    // Routes: set associated track if any.
    let typeInfo = locationTaxonomy.get({ type, subtype });
    let routes = this.loc.routes;
    if (typeInfo.canHaveRoute() && this.pickTrackElement.track) {
      routes = [this.pickTrackElement.track.path];
    }
    // In case of portage autogenerate the route between landing points
    // if no route yet.
    if (routes.length == 0 && type == 'portage') {
      let landings = rels.filter(rel => rel.type == 'landing');
      if (landings.length > 1) {
        routes = [
          landings.map(loc => ({
            lat: loc.lat,
            lng: loc.lng,
            elevation: 0,
          })),
        ];
      }
    }

    let attributes = {};
    for (let attrName in this.#attrControls) {
      attributes[attrName] = this.#attrControls[attrName].valueAsArray;
    }

    let fields = this.fieldsSpreadsheet.values;
    let state = (this.stateCheckbox.checked && 'private') || null;

    let cover = this.#cover;
    let pics = this.picListElement.pics;

    let locData = {
      id,
      name,
      description,
      type,
      subtype,
      google_id,
      lat,
      lng,
      routes,
      rels,
      relatedIds,
      attributes,
      fields,
      state,
      cover,
      pics,
    };

    this.dispatchEvent(
      new CustomEvent('locchange', {
        bubbles: true,
        detail: {
          locData,
          track: this.pickTrackElement.track,
        },
      })
    );
  }

  #saveOnClick() {
    this.saveSubmitButton.click();
  }

  #deleteOrReport() {
    if (this.loc.readonly) {
      return ReportDialog.show({
        data: {
          detailsData: this.loc,
          detailsMarkup: `<span class='designation'>${this.loc.name}</span> location`,
        },
        onaccept: data => this.#fireLocReport(data),
      });
    }

    return LocDeleteDialog.show({
      data: this.loc,
      onaccept: () => this.#fireLocDelete(),
    });
  }

  #locValuePasted(ev) {
    let text = ev.clipboardData.getData('text');
    let match = text.match(/[-]?[0-9]{1,3}[.][0-9]{0,7}/);
    if (match) {
      ev.target.value = match[0];
      ev.preventDefault();
      this.#firePosChange();
    }
  }

  #guessLocTypeFromName(name) {
    let typeMatch = locationTaxonomy.typeByName(name);
    if (typeMatch && this.typeElement.category != typeMatch[0]) {
      this.typeElement.set(...typeMatch);
      this.setTypeDependentControls(typeMatch[0]);
      return true;
    }
    return false;
  }

  #fireLocDelete() {
    this.dispatchEvent(
      new CustomEvent('locdelete', {
        bubbles: true,
        detail: this.loc,
      })
    );
  }

  #fireLocReport(detail) {
    this.dispatchEvent(
      new CustomEvent('locreport', {
        bubbles: true,
        detail,
      })
    );
  }

  #firePosChange() {
    this.dispatchEvent(
      new CustomEvent('locposchange', {
        bubbles: true,
        detail: {
          lat: this.latInput.valueAsNumber,
          lng: this.lngInput.valueAsNumber,
        },
      })
    );
  }

  #attrControls;
  #cover;
  #editModeDropDefault;
}

LocEdit.defineCustomElement();

export { LocEdit };
